import { CaretDown, CaretUp, Download } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'
import {
  downloadSalesforcePSLReport,
  downloadSalesforceReport,
  getSalasForceUserPaginatedData,
  getSalesforceUserPSLPaginatedData
} from '../../api/salesforceAudit/salesforceAudit.service'

import { Button } from '../../components/New/Button/Button'
import ContextSwitcherTabs from '../../components/common/DDS/ContextSwitcherTab/ContextSwitcherTab'
import { DDSTypography } from 'den-design-system'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import { PaginatedTable } from '../../components/SalesforceAudit/SFPaginatedTable'
import SFLicenseUsageCard from '../../components/SalesforceAudit/SFLicenseUsageCard'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Spinner from '../../components/common/Spinner/Spinner'
import ToastNotification from '../../components/common/DDS/Toast/Toast'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import { convertJsonToCsvDownload } from '../../utils/FileUtils'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { timeZone } from '../../utils/DateTimeUtils'
import { useParams } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFLicenseDetails: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const params = useParams()
  const store = useStoreContext()

  const { salesforceAuditStore } = useStoreContext()
  const [isUserAvailable, setIsUserAvailable] = useState(false)
  const searchParams = new URLSearchParams(location.search)
  const tab = searchParams.get('tab')
  const [userTabIndex, setUserTabIndex] = useState<string>(
    searchParams.get('contextSwitcherTab') ?? '1'
  )
  const data =
    salesforceAuditStore.getLicenseById(params.id!) ||
    salesforceAuditStore.getPermissionSetsLicensesById(params.id!)
  const availableLicenses = data?.TotalLicenses - data?.UsedLicenses

  const [usersWithLicense, setUsersWithLicense] = useState<any[]>([])
  const [usersWithPermissionLicense, setUsersWithPermissionLicense] = useState<
    any[]
  >([])

  const [loading, setLoading] = useState(false)
  const salesforceAuditId = salesforceAuditStore.auditData?.salesforceAuditId

  const [totalCount, setTotalCount] = useState<number>(0)
  const [permissionUserCount, setPermissionUserCount] = useState(0)

  const fetchSalesforcePaginatedUsers = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    try {
      const response: any = await getSalasForceUserPaginatedData({
        $salesforceAuditId: salesforceAuditId as string,
        profileUserLicenseId: params.id!,
        page: currentPage,
        isActive: userTabIndex === '1' ? true : false,
        size: SalesforceAuditStore.userPageSize
      })

      if (response?.data) {
        setTotalCount(response.totalCount)
        setUsersWithLicense(response.data)
        salesforceAuditStore.setUserLicensePaginatedData(response.data)
      }
    } catch (err) {
      console.error('Error fetching Salesforce users:', err)
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  const fetchSalesforcePaginatedPermissionUsers = async () => {
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    try {
      const response: any = await getSalesforceUserPSLPaginatedData({
        $salesforceAuditId: salesforceAuditId as string,
        permissionSetLicenseId: params.id!,
        page: currentPage,
        isActive: userTabIndex === '1' ? true : false,
        size: SalesforceAuditStore.userPageSize
      })

      if (response?.data) {
        setUsersWithPermissionLicense(response.data)
        setPermissionUserCount(response.totalCount)
        salesforceAuditStore.setUserLicensePaginatedData(response.data)
      }
    } catch (err) {
      console.error('Error fetching Salesforce users:', err)
    } finally {
      setLoading(false)
      store.uiStore.setGlobalLoader(false)
    }
  }

  useEffect(() => {
    if (salesforceAuditId && params.id && tab === '1') {
      fetchSalesforcePaginatedUsers()
    } else {
      fetchSalesforcePaginatedPermissionUsers()
    }
  }, [salesforceAuditId, params.id, userTabIndex, currentPage])

  const filteredUsers = useMemo(() => {
    return usersWithLicense.filter(
      (user: any) => user?.profileUserLicenseId === params.id
    )
  }, [usersWithLicense, params.id, currentPage])

  const filteredPermissionUsers = useMemo(() => {
    const allAssignments = usersWithPermissionLicense.flatMap((user: any) => {
      const assignments = user.user || []

      return assignments
    })

    return allAssignments
  }, [usersWithPermissionLicense, params.id, currentPage])

  const selectedUsers = tab === '2' ? filteredPermissionUsers : filteredUsers

  const paramTab = new URLSearchParams(location.search)

  const tabIndex = paramTab.get('tab')

  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)

  const usage =
    data?.TotalLicenses < 0
      ? 0
      : +((data?.UsedLicenses / data?.TotalLicenses) * 100).toFixed(2)

  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return selectedUsers

    return selectedUsers.filter((item: any) => {
      // Use top-level fields for tab 2, else use record
      const source = tab === '2' ? item : item.record || {}

      const name = (source.Name || '').toLowerCase()
      const email = (source.Email || '').toLowerCase()
      const profile = (source.Profile?.Name || '').toLowerCase()

      switch (searchColumn) {
        case 'Name':
          return name.includes(trimmedSearch)
        case 'Email':
          return email.includes(trimmedSearch)
        case 'Profile':
          return profile.includes(trimmedSearch)
        case 'all':
        default:
          return (
            name.includes(trimmedSearch) ||
            email.includes(trimmedSearch) ||
            profile.includes(trimmedSearch)
          )
      }
    })
  }, [selectedUsers, activeSearchTerm, searchColumn, tab])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData

    return [...filteredData].sort((a: any, b: any) => {
      const sourceA = tab === '2' ? a : a.record || {}
      const sourceB = tab === '2' ? b : b.record || {}

      let valA = ''
      let valB = ''

      switch (sortConfig.key) {
        case 'Name':
          valA = (sourceA.Name || '').toLowerCase()
          valB = (sourceB.Name || '').toLowerCase()
          break
        case 'Email':
          valA = (sourceA.Email || '').toLowerCase()
          valB = (sourceB.Email || '').toLowerCase()
          break
        case 'Profile':
          valA = (sourceA.Profile?.Name || '').toLowerCase()
          valB = (sourceB.Profile?.Name || '').toLowerCase()
          break
        default:
          valA = (sourceA[sortConfig.key!] || '').toString().toLowerCase()
          valB = (sourceB[sortConfig.key!] || '').toString().toLowerCase()
          break
      }

      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }

      return sortConfig.direction === 'asc'
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA)
    })
  }, [filteredData, sortConfig, salesforceAuditStore, tab])

  useEffect(() => {
    const organizationId = store.scopeStore.getScope()[
      'organization-id'
    ] as string
    const projectId = store.scopeStore.getScope()['project-id'] as string
    const environmentId = store.scopeStore.getScope()[
      'environment-id'
    ] as string

    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          organizationId,
          projectId,
          environmentId
        )
      },
      {
        label: (
          <div>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              Licenses
            </DDSTypography.Paragraph>
          </div>
        ),
        link: `${replaceUrl(
          routeConstants.SALESFORCE_AUDIT_LICENSES_OVERVIEW,
          organizationId,
          projectId,
          environmentId
        )}?tab=${tabIndex}`
      },
      {
        label: data?.Name || data?.MasterLabel,
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_LICENSES_OVERVIEW,
          organizationId,
          projectId,
          environmentId
        )
      }
    ])
  }, [tabIndex])
  const userPaginatedData = useMemo(() => {
    return sortedData.map((item: any) => (tab === '2' ? item : item?.record))
  }, [sortedData, tab])

  const Columns = [
    {
      columnHeaderStyle: { width: '500px' },
      columnDataStyle: { width: '500px' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              User Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[300px] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 28 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Email
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Email')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Email' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Email' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Email',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              color={pallete.colors.textDark4}
            >
              {text?.toLocaleString('en-US')}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    },

    {
      columnHeaderStyle: {},
      columnDataStyle: {},
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Profile
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('Profile')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'Profile' &&
                  sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'Profile',
      render: (text: any) => {
        return (
          <div className='flex items-start justify-start gap-[8px] min-w-[200px]'>
            <div>
              <Tooltip
                id={`tooltip-name-${text?.Name}`}
                label={text?.Name}
                className={`override-tooltip-arrow max-w-[90%] break-all ${
                  typeof text?.Name === 'string' && text?.Name.length > 25
                    ? text
                    : '!hidden'
                }`}
              >
                <div>
                  <DDSTypography.Paragraph
                    size='para'
                    variant='medium'
                    color={pallete.colors.textDark4}
                    className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                  >
                    {text?.Name || text?.Name === 0 ? text?.Name : '-'}
                  </DDSTypography.Paragraph>
                </div>
              </Tooltip>
            </div>
          </div>
        )
      }
    }
  ]

  const ContextSwitcherTab = [
    {
      id: '1',
      label: 'Active Users',
      children: (
        <PaginatedTable
          data={userPaginatedData}
          columns={Columns}
          currentPage={currentPage}
          rowsPerPage={SalesforceAuditStore.userPageSize}
          totalCount={tab === '1' ? totalCount : permissionUserCount}
          onPageChange={(page) => setCurrentPage(page)}
          searchTerm={searchTerm}
          noDataText='No Licenses are currently assigned.'
          noSearchMatchText='No Licenses match your search input.'
          noDataImage={NoRecordData}
          noSearchImage={NoRecordImage}
        />
      )
    },
    {
      id: '2',
      label: 'Inactive Users',
      children: (
        <PaginatedTable
          data={userPaginatedData}
          columns={Columns}
          currentPage={currentPage}
          rowsPerPage={SalesforceAuditStore.userPageSize}
          totalCount={tab === '1' ? totalCount : permissionUserCount}
          onPageChange={(page) => setCurrentPage(page)}
          searchTerm={searchTerm}
          noDataText='No Licenses are currently assigned.'
          noSearchMatchText='No Licenses match your search input.'
          noDataImage={NoRecordData}
          noSearchImage={NoRecordImage}
        />
      )
    }
  ]

  const downloadRecords = (licenseId?: string) => {
    store.uiStore.setGlobalLoader(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string,
      profileUserLicenseId: licenseId,
      isActive: userTabIndex === '1' ? true : false,
      format: 'csv'
    }

    downloadSalesforceReport(payload)
      .then((response: any) => {
        const orgTZ = store.userStore.getOrganizationPreference().timeZone
        let usersData: any[] = response.data || []

        if (!usersData.length) {
          ToastNotification({
            type: 'error',
            message: 'No users found for selected filters'
          })
          return
        }

        usersData = usersData.map((user) => ({
          email: user.email || '-',
          fullName: user.fullName || '-',
          profile: user.profile || '-',
          role: user.role || '-',
          isActive: user.isActive ?? false,
          lastLoginDate:
            user.lastLoginDate && user.lastLoginDate !== '-'
              ? timeZone(
                  orgTZ,
                  user.lastLoginDate,
                  timeFormats.fullMonthDateYearTime
                )
              : '-',
          licenseId: user.licenseId || '-',
          licenseName: user.licenseName || '-'
        }))

        if (!usersData.length) {
          ToastNotification({
            type: 'error',
            message: 'No users found for selected filters'
          })
          return
        }

        const date = new Date()
        const formattedDate = `${(date.getMonth() + 1)
          .toString()
          .padStart(
            2,
            '0'
          )}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`

        convertJsonToCsvDownload(
          usersData,
          `Salesforce_User_With_Licenses_Report_${formattedDate}.csv`
        )

        ToastNotification({
          type: 'success',
          message: `Successfully exported ${usersData.length} user(s)`
        })
      })
      .catch(() => {
        ToastNotification({
          type: 'error',
          message: 'Error downloading Salesforce report'
        })
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
      })
  }

  const downloadPslRecords = (licenseId?: string) => {
    store.uiStore.setGlobalLoader(true)

    const payload = {
      salesforceAuditId: salesforceAuditId as string,
      permissionSetLicenseId: licenseId,
      isActive: userTabIndex === '1' ? true : false,
      format: 'csv'
    }

    downloadSalesforcePSLReport(payload)
      .then((response: any) => {
        const orgTZ = store.userStore.getOrganizationPreference().timeZone
        const usersData: any[] = response.data || []

        if (!usersData.length) {
          ToastNotification({
            type: 'error',
            message: 'No users found for selected filters'
          })
          return
        }

        const normalizedData = usersData.map((record) => {
          let user = record.user

          if (typeof user === 'string') {
            try {
              user = JSON.parse(user)
            } catch (err) {
              console.error('Failed to parse user record:', user, err)
              user = {}
            }
          }

          const matchedPSL = user?.pslAssignments?.find(
            (psl: any) => psl.licenseId === licenseId
          )

          return {
            email: user?.email || user?.Email || '-',
            fullName: user?.fullName || user?.Name || '-',
            profile: user?.profile || user?.Profile?.Name || '-',
            role: user?.role || user?.UserRole?.Name || '-',
            isActive: user?.isActive ?? user?.IsActive ?? false,
            lastLoginDate:
              user?.lastLoginDate && user?.lastLoginDate !== '-'
                ? timeZone(
                    orgTZ,
                    user.lastLoginDate,
                    timeFormats.fullMonthDateYearTime
                  )
                : '-',
            permissionSetLicenseId: matchedPSL?.licenseId || '-',
            permissionSetLicenseName: matchedPSL?.licenseName || '-'
          }
        })

        const date = new Date()
        const formattedDate = `${(date.getMonth() + 1)
          .toString()
          .padStart(
            2,
            '0'
          )}-${date.getDate().toString().padStart(2, '0')}-${date.getFullYear()}`

        convertJsonToCsvDownload(
          normalizedData,
          `Salesforce_PSL__User_With_Licenses_Report_${formattedDate}.csv`
        )

        ToastNotification({
          type: 'success',
          message: `Successfully exported ${normalizedData.length} user(s)`
        })
      })
      .catch(() => {
        ToastNotification({
          type: 'error',
          message: 'Error downloading Salesforce report'
        })
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
      })
  }

  return (
    <>
      {loading ? (
        <div className='flex items-center justify-center h-screen w-full'>
          <Spinner />
        </div>
      ) : (
        <div className='flex flex-col w-full'>
          <div className='flex flex-col pt-[24px]'>
            <SFLicenseUsageCard
              usage={usage}
              totalLicenses={data?.TotalLicenses}
              usedLicenses={data?.UsedLicenses}
              availableLicenses={availableLicenses < 0 ? 0 : availableLicenses}
            />
          </div>

          <div className='flex flex-col w-full py-[24px]'>
            <div className='flex items-center justify-between pb-[12px]'>
              <DDSTypography.Title
                type='h5'
                variant='medium'
                color={pallete.colors.black}
              >
                {`Users with this license (${tab === '1' ? totalCount : permissionUserCount})`}
              </DDSTypography.Title>

              <Button
                id='outline-button'
                label='Export'
                disabled={
                  store.uiStore.getGlobalLoader() ||
                  (tabIndex === '1' && totalCount === 0) ||
                  (tabIndex === '2' && permissionUserCount === 0)
                }
                type='neutral'
                size='small'
                onClick={() => {
                  if (tabIndex === '1') {
                    downloadRecords(params.id!)
                  } else {
                    downloadPslRecords(params.id!)
                  }
                }}
                startIcon={<Download color={pallete.colors.neuButtonText} />}
              />
            </div>
            <div className='relative pb-[12px]'>
              <Search
                id='search'
                name='Search'
                placeholder='Search'
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value
                  setSearchTerm(value)
                  setShowDropdown(value.length > 0)
                  if (value.trim() === '') {
                    setActiveSearchTerm('')
                  }
                }}
                autoComplete='off'
                width='100%'
                onPressEnter={() => {
                  setActiveSearchTerm(searchTerm.trim())
                  setCurrentPage(1)
                  setShowDropdown(false)
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowDropdown(false)
                    setCurrentPage(1)
                  }, 200)
                }}
              />

              {showDropdown && (
                <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                  {[
                    { label: 'Name', value: 'Name' },
                    { label: 'Email', value: 'Email' },
                    { label: 'Profile', value: 'Profile' },
                    { label: 'All Columns', value: 'all' }
                  ].map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setSearchColumn(option.value)
                        setCurrentPage(1)
                        setActiveSearchTerm(searchTerm)
                        setShowDropdown(false)
                      }}
                      className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                    >
                      <span className='font-mulish text-black'>
                        {searchTerm}
                      </span>
                      <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                        in: {option.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className='  flex items-start w-full'>
              <div className='flex-grow '>
                <ContextSwitcherTabs
                  tabOptions={ContextSwitcherTab}
                  shape='roundedRectangle'
                  tabStyle={{
                    border: '1px solid',
                    borderColor: pallete.colors.stroke2,
                    userSelect: 'none',
                    width: '300px'
                  }}
                  tabTitleClassName='text-center select-none'
                  activeTab={userTabIndex}
                  onChange={(index: any) => {
                    setUserTabIndex(index)
                    setCurrentPage(1)
                  }}
                  disabled={loading || store.uiStore.getGlobalLoader()}
                  className='override-height override-view-more-tabs'
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default SFLicenseDetails
